import {
  Checkbox,
  css,
  FormControlLabel,
  Portal,
  useTheme
} from '@mui/material'
import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import { ChangeEvent, useState } from 'react'
import toast, { Toaster } from 'react-hot-toast'
import { match } from 'ts-pattern'
import Close from 'assets/icons/close.svg'
import badge_course from 'assets/images/badge_course.png'
import badge_cycling from 'assets/images/badge_cycling.png'
import badge_trail from 'assets/images/badge_trail.png'
import badge_triathlon from 'assets/images/badge_triathlon.png'
import IconButton, {
  IconButtonSize,
  IconButtonVariation
} from 'components/IconButton'
import Modal from 'components/Modal'
import { apiClient } from 'services/api/client'
import {
  OpenContestEdition,
  SendVoteResult
} from 'services/api/database/ContestService'
import { Theme } from 'styles/theme'
import {
  BadgeContainer,
  CheckboxContainer,
  ConditionsLink,
  EmailInput,
  HowItWorksLink,
  InputWrapper,
  ModalContent,
  ModalHeading,
  ModalText,
  VoteCardContainer
} from './VoteCard.style'
import { Button, ButtonVariant } from './common/Button'

import '@algolia/autocomplete-theme-classic'

interface VoteCardEvent {
  id: number
  city: string
  countrySubdivisionDisplayCodeLevel2: string
  name: string
}

interface VoteCardProps {
  event?: VoteCardEvent
  badge?: string | null
  contest: OpenContestEdition
  transparent?: boolean
}

export default function VoteCard({
  event,
  badge,
  contest,
  transparent
}: VoteCardProps) {
  const { t } = useTranslation('vote')
  const theme: Theme = useTheme()

  const [isParticipationChecked, setParticipationCheck] = useState(false)
  const [isMarketingChecked, setMarketingCheck] = useState(false)
  const [emailConcours, setEmailConcours] = useState('')

  const [isModalHowOpen, setModalHowOpen] = useState(false)

  const [isModalStep1Open, setModalStep1Open] = useState(false)
  const [isModalStep2Open, setModalStep2Open] = useState(false)
  const [isModalStep3Open, setModalStep3Open] = useState(false)
  const [sendingVote, setSendingVote] = useState(false)

  const [eventSelected] = useState<VoteCardEvent | undefined>(event)

  // const onSuggestionSelected = (eventInfo: VoteCardEvent) => {
  //   setEventSelected(eventInfo)
  //   setModalStep1Open(false)
  //   setModalStep2Open(true)
  // }

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmailConcours(event.target.value)
  }

  const isEmailIsCorrect = () => {
    if (emailConcours.match(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/)) return true
    return false
  }

  const saveEmail = async (nextStep: boolean) => {
    const eventId = eventSelected?.id
    setSendingVote(true)
    try {
      const { data } = await apiClient.post<SendVoteResult>(
        `/contests/${contest.id}/sendVote`,
        {
          eventId,
          isMarketingChecked,
          email: emailConcours
        }
      )

      if (data.type === 'SendVoteSuccess') {
        toast.success(t('toast.successVote'))
        if (nextStep) {
          setModalStep2Open(false)
          setModalStep3Open(true)
        }
      } else toast.error(t('modal.error.voteRestriction'))
    } catch (error) {
      console.error(error)
      toast.error(t('toast.errorVote'))
    } finally {
      setSendingVote(false)
    }
  }

  const badgeSrc = match(badge)
    .with('TRAIL', () => badge_trail)
    .with('RUNNING', () => badge_course)
    .with('TRIATHLON', () => badge_triathlon)
    .with('CYCLING', () => badge_cycling)
    .otherwise(() => null)

  return (
    <>
      <VoteCardContainer theme={theme} transparent={transparent}>
        {badgeSrc && (
          <BadgeContainer>
            <Image
              src={badgeSrc}
              width={250}
              height={160}
              alt="badge-trail-concours"
            />
          </BadgeContainer>
        )}

        <Button
          label={
            event && event.id
              ? t('button.voteForThisRace')
              : t('button.voteForARace')
          }
          variant={ButtonVariant.Secondary}
          fullWidth
          onClick={() => {
            if (event && event.id) {
              !isModalStep1Open
                ? setModalStep2Open(true)
                : setModalStep2Open(false)
            } else {
              !isModalStep1Open
                ? setModalStep1Open(true)
                : setModalStep1Open(false)
            }
          }}
        />
        <HowItWorksLink
          href=""
          theme={theme}
          onClick={(e) => {
            e.preventDefault()
            !isModalHowOpen ? setModalHowOpen(true) : setModalHowOpen(false)
          }}
        >
          {t('link.howItWorks')}
        </HowItWorksLink>
      </VoteCardContainer>

      <Modal
        isOpen={isModalStep1Open}
        closeModal={() => setModalStep1Open(false)}
        contentLabel={t('modal.step1.contentLabel')}
        contentStyle={{ height: 'fit-content' }}
      >
        <Close
          onClick={() => {
            setModalStep1Open(false)
          }}
          css={css`
            cursor: pointer;
            position: absolute;
            right: 35px;
            height: 20px;
          `}
        />
        <ModalContent>
          <ModalHeading>{t('modal.step1.title')}</ModalHeading>
          <ModalText>{t('modal.step1.description')}</ModalText>
          <div
            css={css`
              margin: auto;
              width: 100%;
              max-width: 450px;
              border-radius: 8px;
              box-shadow: 0 0 0 1px ${theme.palette.surface.secondary};
            `}
          >
            {/* TODO: implemente new autocomplete */}
            {/* <AutocompleteVote
              year={contest.year}
              raceTypes={contest.raceTypes}
              onSuggestionSelected={({
                objectID,
                eventCity,
                eventName,
                eventCountrySubdivisionDisplayCodeLevel2
              }) => {
                onSuggestionSelected({
                  id: Number(objectID),
                  city: eventCity,
                  name: eventName,
                  countrySubdivisionDisplayCodeLevel2:
                    eventCountrySubdivisionDisplayCodeLevel2
                })
              }}
            /> */}
          </div>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isModalStep2Open}
        closeModal={() => setModalStep2Open(false)}
        contentLabel={t('modal.step2.contentLabel')}
        contentStyle={{ height: 'fit-content' }}
      >
        <Close
          onClick={() => {
            setEmailConcours('')
            setParticipationCheck(false)
            setModalStep2Open(false)
          }}
          css={css`
            cursor: pointer;
            position: absolute;
            right: 35px;
            height: 20px;
          `}
        />
        <ModalContent>
          <ModalHeading>{t('modal.step2.title')}</ModalHeading>
          <ModalText>
            <span
              css={css`
                font-weight: 700;
              `}
            >
              {eventSelected?.name}
            </span>
            <br />
            <span
              css={css`
                color: #525252;
              `}
            >
              {eventSelected?.city} (
              {eventSelected?.countrySubdivisionDisplayCodeLevel2})
            </span>
            <br />
            <br />
            {t('modal.step2.instructions.register')}
            <br />
            {t('modal.step2.instructions.confirm')}
            <br />
            {t('modal.step2.instructions.confirmed')}
          </ModalText>
          <InputWrapper>
            <EmailInput type="email" placeholder="Email" onChange={onChange} />
            <IconButton
              icon={sendingVote ? 'progress' : 'arrowRight'}
              size={IconButtonSize.regular}
              variation={IconButtonVariation.primary}
              css={css`
                height: 50px;
                border-top-left-radius: 0px;
                border-bottom-left-radius: 0px;
              `}
              isDisabled={
                isEmailIsCorrect() && isParticipationChecked ? false : true
              }
              onClick={() => {
                if (isEmailIsCorrect() && isParticipationChecked)
                  saveEmail(true)
              }}
            />
          </InputWrapper>

          <CheckboxContainer>
            <FormControlLabel
              control={
                <Checkbox
                  name="participationChecked"
                  id="participationChecked"
                  onChange={() => {
                    setParticipationCheck(isParticipationChecked ? false : true)
                  }}
                />
              }
              label={t('modal.step2.participationLabel')}
            />
            <br />
            <FormControlLabel
              control={
                <Checkbox
                  name="marketingCheckbox"
                  id="marketingCheckbox"
                  onChange={() => {
                    setMarketingCheck(isMarketingChecked ? false : true)
                  }}
                />
              }
              label={t('modal.step2.marketingLabel')}
            />
            <br />
            <ConditionsLink>
              <a
                css={css`
                  color: ${theme.palette.label.default};
                `}
                target="blank"
                href="https://docs.google.com/document/d/1qQqYlwPevie3omKWDMPNFY-9IK2yVJEB-Xf3t7tAdEA/edit?usp=sharing"
              >
                {t('modal.step2.conditionsLink')}
              </a>
            </ConditionsLink>
          </CheckboxContainer>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isModalStep3Open}
        closeModal={() => setModalStep3Open(false)}
        contentLabel={t('modal.step3.contentLabel')}
        contentStyle={{ height: 'fit-content' }}
      >
        <Close
          onClick={() => {
            setEmailConcours('')
            setParticipationCheck(false)
            setModalStep3Open(false)
          }}
          css={css`
            cursor: pointer;
            position: absolute;
            right: 35px;
            height: 20px;
          `}
        />
        <ModalContent>
          <ModalHeading>{t('modal.step3.title')}</ModalHeading>
          <ModalText>
            <span
              css={css`
                font-weight: 700;
              `}
            >
              {eventSelected?.name}
            </span>
            <br />
            <span
              css={css`
                color: #525252;
              `}
            >
              {eventSelected?.city} (
              {eventSelected?.countrySubdivisionDisplayCodeLevel2})
            </span>
            <br />
            <br />
            {t('modal.step3.reminder.lastStep')}{' '}
            <span
              css={css`
                font-weight: 700;
              `}
            >
              {t('modal.step3.reminder.voteNotRegisteredYet')}{' '}
            </span>
            {t('modal.step3.reminder.goToEmail')}
          </ModalText>
          <a
            css={css`
              color: #c4c4c4;
              cursor: pointer;
              text-align: center;
            `}
            onClick={() => {
              saveEmail(false)
            }}
          >
            {t('modal.step3.resendEmail')}
          </a>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isModalHowOpen}
        closeModal={() => setModalHowOpen(false)}
        contentLabel={t('modal.how.contentLabel')}
        contentStyle={{ height: 'fit-content' }}
      >
        <Close
          onClick={() => setModalHowOpen(false)}
          css={css`
            cursor: pointer;
            position: absolute;
            right: 35px;
            height: 20px;
          `}
        />
        <ModalContent>
          <ModalHeading>{t('modal.how.title')}</ModalHeading>
          {/* <ModalText>{t('modal.how.description')}</ModalText> */}
          <ModalText
            dangerouslySetInnerHTML={{
              __html: t('modal.how.description')
            }}
          />
        </ModalContent>
      </Modal>
      <Portal>
        <Toaster position="bottom-center" reverseOrder={false} />
      </Portal>
    </>
  )
}
